import { Box, Typography, SxProps, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ReactNode } from 'react';
import { SvgIconComponent } from '@mui/icons-material';
import CopySection from '../businesses/SettingsSection/CopySection';

interface CenteredContentLayoutProps {
  image:
    | {
        src: string;
        alt: string;
      }
    | SvgIconComponent;
  title: string;
  description: string;
  button?: {
    text: string;
    onClick: () => void;
    loading?: boolean;
  };
  secondaryButton?: {
    icon?: SvgIconComponent;
    text: string;
    onClick: () => void;
    loading?: boolean;
  };
  copyContent?: string;
  maxWidth?: string | number;
  children?: ReactNode;
  containerSx?: SxProps;
  imageMaxWidth?: string | number;
}

export default function CenteredContentLayout({
  image,
  title,
  description,
  button,
  secondaryButton,
  copyContent,
  maxWidth = '768px',
  children,
  containerSx,
  imageMaxWidth = '280px',
}: CenteredContentLayoutProps) {
  const SecondaryButtonIcon = secondaryButton?.icon;

  const isImageIcon = !('src' in image);

  const ImageIcon = isImageIcon ? image : undefined;

  return (
    <Stack
      position="relative"
      width="100%"
      minHeight="100%"
      alignSelf="stretch"
      alignItems="center"
      justifyContent="center"
      gap={2}>
      {secondaryButton && (
        <LoadingButton
          onClick={secondaryButton.onClick}
          loading={secondaryButton.loading}
          variant="light"
          sx={{
            alignSelf: 'end',
            mb: 'auto',
          }}
          startIcon={
            SecondaryButtonIcon ? (
              <SecondaryButtonIcon width={16} height={16} />
            ) : undefined
          }>
          <span>{secondaryButton.text}</span>
        </LoadingButton>
      )}
      <Box
        my={secondaryButton ? 'auto' : undefined}
        width="100%"
        maxWidth={maxWidth}
        mx="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={4}
        sx={containerSx}>
        <Box
          width="100%"
          maxWidth={imageMaxWidth}
          sx={{
            aspectRatio: '1/1',
          }}>
          {!isImageIcon ? (
            <img
              src={image.src}
              alt={image.alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : ImageIcon ? (
            <ImageIcon sx={{ width: '100%', height: '100%' }} />
          ) : null}
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="h1" textAlign="center">
            {title}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {description}
          </Typography>
        </Box>

        {copyContent && (
          <CopySection
            copy={{
              content: copyContent,
            }}
            containerSx={{
              width: '100%',
            }}
          />
        )}

        {button && (
          <LoadingButton
            sx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
            onClick={button.onClick}
            loading={button.loading}
            variant="base"
            color="primary">
            <Typography variant="inherit" fontWeight={600}>
              {button.text}
            </Typography>
          </LoadingButton>
        )}
        <Box width="100%" display="flex" flexDirection="column" gap={4}>
          {children}
        </Box>
      </Box>
    </Stack>
  );
}
